import { ArrowUpRight, Disc3, ExternalLink, Headphones, Mail, Radio, Send, ShieldCheck, Video } from "lucide-react";
import { FormEvent, useState } from "react";
import logoUrl from "../EBA64CC8-7559-4F36-AAF4-323EDEDD880D.png";
import { apiBaseUrl, siteUrl } from "./config";
import { ReleaseCatalog } from "./ReleaseCatalog";
import { ReleasePage } from "./ReleasePage";

type FormStatus = "idle" | "loading" | "success" | "error";

const links = [
  { label: "Spotify", url: "https://open.spotify.com/artist/24BRdp0OWNx6BBxRuVlFBf", icon: Headphones },
  { label: "Apple Music", url: "https://music.apple.com/us/artist/moneymase/1844690843", icon: Disc3 },
  { label: "YouTube", url: "https://youtube.com/@official.moneymase", icon: Video },
  { label: "TikTok", url: "https://TikTok.com/@official.moneymase", icon: Radio },
  { label: "Amazon Music", url: "https://music.amazon.com/artists/B09V7SJG39/moneymase", icon: ExternalLink }
];

async function postJson(path: string, body: unknown) {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });

  if (!response.ok) throw new Error("Request failed");
  return response.json();
}

function App() {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<FormStatus>("idle");
  const [contactStatus, setContactStatus] = useState<FormStatus>("idle");

  const parts = window.location.pathname.split("/").filter(Boolean);
  if (parts[0] === "releases" && parts[1]) {
    return <ReleasePage slug={parts[1]} />;
  }

  async function submitNewsletter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNewsletterStatus("loading");
    try {
      await postJson("/api/newsletter", { email: newsletterEmail, source: "website-footer" });
      setNewsletterEmail("");
      setNewsletterStatus("success");
    } catch {
      setNewsletterStatus("error");
    }
  }

  async function submitContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setContactStatus("loading");
    try {
      await postJson("/api/contact", Object.fromEntries(new FormData(event.currentTarget).entries()));
      event.currentTarget.reset();
      setContactStatus("success");
    } catch {
      setContactStatus("error");
    }
  }

  return (
    <>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Moneymase home">
          <img
            src={logoUrl}
            alt=""
            style={{
              width: "42px",
              height: "42px",
              objectFit: "contain",
              filter: "drop-shadow(0 0 12px rgba(217, 31, 47, 0.42))"
            }}
          />
          <span>Moneymase</span>
        </a>
        <nav className="nav-links" aria-label="Primary navigation"><a href="#music">Music</a><a href="#releases">Releases</a><a href="#about">About</a><a href="#contact">Contact</a></nav>
      </header>

      <main id="top">
        <section className="hero" aria-labelledby="hero-title">
          <img className="hero-backdrop" src="/assets/hero-stage.png" alt="" />
          <div className="hero-shade" />
          <div className="hero-content">
            <img
              src={logoUrl}
              alt="MoneyMase logo"
              style={{
                display: "block",
                width: "min(420px, 88vw)",
                maxHeight: "250px",
                objectFit: "contain",
                margin: "0 0 1.25rem -0.3rem",
                filter: "drop-shadow(0 22px 44px rgba(0, 0, 0, 0.55))"
              }}
            />
            <p className="eyebrow">MMX Records / Independent</p>
            <h1 id="hero-title">Moneymase</h1>
            <p className="hero-copy">Hip hop, rock, pop, metal, and alternative from Mason Peterson. Built in red light, steel nerves, and independent motion.</p>
            <a
              href="https://www.verifiedhuman.info/collective"
              target="_blank"
              rel="noreferrer"
              aria-label="VerifiedHuman artist collective"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.55rem",
                marginTop: "1.3rem",
                minHeight: "42px",
                padding: "0.68rem 0.9rem",
                border: "1px solid rgba(210, 167, 79, 0.55)",
                borderRadius: "999px",
                background: "linear-gradient(135deg, rgba(210, 167, 79, 0.16), rgba(217, 31, 47, 0.12))",
                color: "#f4f0e9",
                fontSize: "0.82rem",
                fontWeight: 900,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                boxShadow: "0 16px 40px rgba(0, 0, 0, 0.28)"
              }}
            >
              <ShieldCheck size={18} aria-hidden="true" />
              <span>VerifiedHuman Artist</span>
              <ArrowUpRight size={16} aria-hidden="true" />
            </a>
            <div className="hero-actions" aria-label="Artist links"><a className="button button-primary" href="#music"><Headphones size={18} aria-hidden="true" />Listen</a><a className="button button-secondary" href="#contact"><Mail size={18} aria-hidden="true" />Booking</a></div>
          </div>
        </section>

        <section className="listen-band" id="music" aria-labelledby="music-title">
          <div className="section-heading"><p className="eyebrow">Official Channels</p><h2 id="music-title">Listen and follow</h2></div>
          <div className="link-grid">{links.map(({ label, url, icon: Icon }) => <a className="platform-card" href={url} key={label} target="_blank" rel="noreferrer"><Icon size={22} aria-hidden="true" /><span>{label}</span><ArrowUpRight size={18} aria-hidden="true" /></a>)}</div>
        </section>

        <ReleaseCatalog />

        <section className="profile-band" id="about" aria-labelledby="about-title">
          <div className="profile-media"><img src="/assets/artist-photo.jpeg" alt="Moneymase artist visual" /></div>
          <div className="profile-copy"><p className="eyebrow">Fairfield / Vacaville / Fargo</p><h2 id="about-title">Independent sound with no single lane</h2><p>Moneymase is an independent artist and MMX Records founder blending hard drums, melodic tension, guitar-driven weight, and direct writing.</p><div className="trait-row"><span>Hip hop pressure</span><span>Rock grit</span><span>Pop hooks</span><span>Metal edge</span><span>Alternative atmosphere</span></div></div>
        </section>

        <section className="contact-band" id="contact" aria-labelledby="contact-title">
          <div className="contact-copy"><p className="eyebrow">Booking / Press / Collabs</p><h2 id="contact-title">Reach the Moneymase camp</h2><p>Use the form for booking, press, playlist, feature, and partnership inquiries.</p></div>
          <form className="contact-form" onSubmit={submitContact}>
            <label>Name<input name="name" autoComplete="name" required minLength={2} maxLength={120} /></label>
            <label>Email<input name="email" type="email" autoComplete="email" required maxLength={180} /></label>
            <label>Topic<select name="topic" defaultValue="booking"><option value="booking">Booking</option><option value="press">Press</option><option value="collab">Collab</option><option value="general">General</option></select></label>
            <label>Message<textarea name="message" required minLength={10} maxLength={1800} rows={5} /></label>
            <button className="button button-primary form-button" disabled={contactStatus === "loading"}><Send size={18} aria-hidden="true" />{contactStatus === "loading" ? "Sending" : "Send"}</button>
            {contactStatus === "success" && <p className="form-note success">Message received.</p>}
            {contactStatus === "error" && <p className="form-note error">Could not send yet. Try again soon.</p>}
          </form>
        </section>
      </main>

      <footer className="site-footer" id="newsletter">
        <div><p className="eyebrow">Moneymase.music</p><h2>Get official updates</h2></div>
        <form className="newsletter-form" onSubmit={submitNewsletter}><label className="sr-only" htmlFor="newsletter-email">Email address</label><input id="newsletter-email" type="email" value={newsletterEmail} onChange={(event) => setNewsletterEmail(event.target.value)} placeholder="Email address" autoComplete="email" required /><button className="button button-secondary" disabled={newsletterStatus === "loading"}><Mail size={18} aria-hidden="true" />Join</button></form>
        {newsletterStatus === "success" && <p className="form-note success">You are on the list.</p>}
        {newsletterStatus === "error" && <p className="form-note error">Signup is not available yet.</p>}
        <p className="footer-meta">© {new Date().getFullYear()} Moneymase / MMX Records. Official site at {siteUrl.replace("https://", "")}.</p>
      </footer>
    </>
  );
}

export default App;